import {
  Button,
  HStack,
  Menu,
  MenuItemProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
  ModalProps,
  Select,
  Text,
  Textarea,
  TextInput,
  VStack,
} from '@televet/kibble-ui';
import {
  ActionEffect,
  ActionEffectType,
  AddAppointmentNoteActionEffect,
  AutomationButtonAction,
  AutomationButtonVariant,
  CreateTaskActionEffect,
  IMessageButtonOption,
  TagConversationActionEffect,
} from '@televet/shared-types/JsonTypes/WorkflowEventActionConfig';
import partial from 'lodash-es/partial';
import {
  automationActionDescriptions,
  automationActionLabels,
} from 'pages/Automations/constants/automationActionLabels';
import {
  updateAutomationPromptButton,
  updateAutomationPromptButtonErrors,
} from 'pages/Automations/state/automationsSlice';
import { convertToSeconds, getSmallestTimeIncrement } from 'pages/Automations/state/utils/time';
import {
  ExtendedScheduleWorkflowActionEffect,
  ScheduledWorkflowDraft,
  TimeIncrement,
} from 'pages/Automations/types/AutomationDraft';
import { ButtonErrorDetail, errorDetails } from 'pages/Automations/types/AutomationValidation';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import withLazyModal from 'shared/components/LazyModal/withLazyModal';
import { FeatureFlagName } from 'shared/enums';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { AutomationRunActionPromptType, WorkflowEvent } from 'shared/types/graphql';
import { useAppDispatch } from 'state/hooks';
import { NextStepSelect } from '../NextStepSelect';
import { StatusChangeSelect } from '../StatusChangeSelect';
import ButtonActionContainer from './ButtonActionContainer';
import ConversationTagMenu from './ConversationTagMenu';
import CreateTaskForm from './CreateTaskForm';
import EditButtonModalSectionHeader from './EditButtonModalSectionHeader';
import ScheduledWorkflowSelector from './ScheduledWorkflowSelector';

const buttonStyleOptions = [
  { label: 'Default', value: AutomationButtonVariant.Secondary },
  { label: 'Strong', value: AutomationButtonVariant.Primary },
  { label: 'Subtle', value: AutomationButtonVariant.GhostNeutral },
];

const buttonActionOptions = [
  { label: 'No Change', value: ActionEffectType.None },
  { label: 'Confirmed', value: ActionEffectType.ConfirmAppointment },
];

interface EditButtonModalProps extends Pick<ModalProps, 'isOpen'> {
  button: IMessageButtonOption;
  buttonIndex: number;
  buttonErrors: ButtonErrorDetail | undefined;
  promptType: AutomationRunActionPromptType;
  promptIndex: number;
  onClose: (revertChanges?: boolean) => void;
}

const EditButtonModal = ({
  button,
  buttonIndex,
  buttonErrors,
  promptType,
  promptIndex,
  isOpen,
  onClose,
}: EditButtonModalProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { isFeatureEnabled } = useFeatureFlag();
  const { isWritebackApptNotesSupported } = useIntegrationsProvider();

  const [buttonDraft, setButtonDraft] = useState<IMessageButtonOption>(button);
  const [selectedButtonActions, setSelectedButtonActions] = useState<AutomationButtonAction[]>([
    ...(!!button.channelStatusChangeId ? [AutomationButtonAction.SetConversationStatus] : []),
    ...(button.entityAction === ActionEffectType.ConfirmAppointment ||
    button.actionEffects?.some((effect) =>
      [ActionEffectType.ConfirmAppointment, ActionEffectType.AddAppointmentNote].includes(effect.type),
    )
      ? [AutomationButtonAction.SetAppointmentStatus]
      : []),
    ...(button.entityAction === ActionEffectType.AddTagsToChannel ||
    button.actionEffects?.some((effect) => effect.type === ActionEffectType.AddTagsToChannel)
      ? [AutomationButtonAction.AddConversationTags]
      : []),
    ...(button.actionEffects?.some((effect) => effect.type === ActionEffectType.ScheduleWorkflow)
      ? [AutomationButtonAction.ScheduleWorkflow]
      : []),
    ...(button.actionEffects?.some((effect) => effect.type === ActionEffectType.CreateTask)
      ? [AutomationButtonAction.CreateTask]
      : []),
  ]);
  const [showAppointmentNotes, setShowAppointmentNotes] = useState(
    button.actionEffects?.some((effect) => effect.type === ActionEffectType.AddAppointmentNote),
  );

  const [scheduledWorkflow, setScheduledWorkflow] = useState<ScheduledWorkflowDraft | undefined>(() => {
    const scheduledWorkflowAction = buttonDraft.actionEffects?.find(
      (effect) => effect.type === ActionEffectType.ScheduleWorkflow,
    ) as ExtendedScheduleWorkflowActionEffect;
    const delayValues = getSmallestTimeIncrement(scheduledWorkflowAction?.delayInSeconds, [
      TimeIncrement.Minutes,
      TimeIncrement.Hours,
      TimeIncrement.Days,
    ]);

    return scheduledWorkflowAction
      ? {
          id: scheduledWorkflowAction.workflowId,
          workflowType: scheduledWorkflowAction.workflowType,
          delayAmount: delayValues.amount,
          delayIncrement: delayValues.increment as TimeIncrement,
        }
      : undefined;
  });

  const [buttonActionErrors, _setButtonActionErrors] = useState<
    Partial<Record<AutomationButtonAction, Record<string, boolean>>>
  >({
    [AutomationButtonAction.CreateTask]: {},
    [AutomationButtonAction.ScheduleWorkflow]: {},
    [AutomationButtonAction.SetAppointmentStatus]: {},
    [AutomationButtonAction.SetConversationStatus]: {},
    [AutomationButtonAction.AddConversationTags]: {},
  });

  useEffect(() => {
    if (isOpen) {
      setButtonDraft(button);
    }
  }, [button, isOpen]);

  const setButtonActionErrors = useCallback(
    (action: AutomationButtonAction, errors: Record<string, boolean>) => {
      _setButtonActionErrors((currentErrors) => ({
        ...currentErrors,
        [action]: errors,
      }));
    },
    [_setButtonActionErrors],
  );

  const buttonTextErrors = buttonErrors?.buttonText;
  const buttonUrlErrors = buttonErrors?.externalUrl;

  const handleButtonTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(updateAutomationPromptButton({ promptIndex, buttonIndex, button: { body: event?.target.value || '' } }));

      if (buttonErrors?.buttonText) {
        dispatch(
          updateAutomationPromptButtonErrors({
            promptIndex,
            buttonIndex,
            button: { buttonText: [] },
          }),
        );
      }
    },
    [dispatch, promptIndex, buttonIndex, buttonErrors?.buttonText],
  );

  const handleSetAppointmentStatusChange = useCallback(
    (selectedItem: MenuItemProps) => {
      let actionEffects: ActionEffect[] = [...(buttonDraft.actionEffects || [])];

      if (selectedItem.value === ActionEffectType.ConfirmAppointment) {
        actionEffects.push({ type: ActionEffectType.ConfirmAppointment });
      } else {
        actionEffects = actionEffects.filter(
          (effect) => ![ActionEffectType.ConfirmAppointment, ActionEffectType.AddAppointmentNote].includes(effect.type),
        );
      }

      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: {
            entityAction: selectedItem.value as ActionEffectType,
            actionEffects,
          },
        }),
      );
    },
    [buttonDraft.actionEffects, dispatch, promptIndex, buttonIndex],
  );

  const handleButtonStyleChange = useCallback(
    (selectedItem: MenuItemProps) => {
      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: { style: selectedItem.value as AutomationButtonVariant },
        }),
      );
    },
    [buttonIndex, dispatch, promptIndex],
  );

  const handleButtonActionSelected = useCallback(
    (selectedItem: MenuItemProps) => {
      setSelectedButtonActions((currentActions) => {
        const action = selectedItem.value as AutomationButtonAction;
        if (currentActions.includes(action)) {
          return currentActions.filter((currentAction) => currentAction !== action);
        }

        setButtonActionErrors(action, {});

        return [...currentActions, action];
      });
    },
    [setButtonActionErrors],
  );

  const removeButtonAction = useCallback(
    (buttonAction: AutomationButtonAction) => {
      if (!selectedButtonActions.includes(buttonAction)) {
        return;
      }

      let buttonConfig: Partial<IMessageButtonOption> = {};
      switch (buttonAction) {
        case AutomationButtonAction.SetConversationStatus:
          buttonConfig = { channelStatusChangeId: undefined };
          break;
        case AutomationButtonAction.SetAppointmentStatus:
          const nonConfirmActionEffects = buttonDraft.actionEffects?.filter(
            (effect) =>
              ![ActionEffectType.ConfirmAppointment, ActionEffectType.AddAppointmentNote].includes(effect.type),
          );
          buttonConfig = { entityAction: ActionEffectType.None, actionEffects: nonConfirmActionEffects };
          break;
        case AutomationButtonAction.AddConversationTags:
          const nonTagActionEffects = buttonDraft.actionEffects?.filter(
            (effect) => effect.type !== ActionEffectType.AddTagsToChannel,
          );
          buttonConfig = { actionEffects: nonTagActionEffects };
          break;
        case AutomationButtonAction.CreateTask:
          const nonTaskActionEffects = buttonDraft.actionEffects?.filter(
            (effect) => effect.type !== ActionEffectType.CreateTask,
          );
          buttonConfig = { actionEffects: nonTaskActionEffects };
          break;
        case AutomationButtonAction.ScheduleWorkflow:
          const nonWorkflowEffects = buttonDraft.actionEffects?.filter(
            (effect) => effect.type !== ActionEffectType.ScheduleWorkflow,
          );
          buttonConfig = { actionEffects: nonWorkflowEffects };
          setScheduledWorkflow(undefined);
          break;
        default:
          break;
      }

      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: {
            ...buttonConfig,
          },
        }),
      );

      setSelectedButtonActions((currentActions) =>
        currentActions.filter((currentAction) => currentAction !== buttonAction),
      );
    },
    [buttonDraft.actionEffects, buttonIndex, dispatch, promptIndex, selectedButtonActions],
  );

  const getDraftActionEffectValue = useCallback(
    (type: ActionEffectType) => {
      if (buttonDraft.entityAction === type) {
        return buttonDraft.entityAction;
      }

      const actionEffect = buttonDraft.actionEffects?.find((effect) => effect.type === type);

      switch (type) {
        case ActionEffectType.AddAppointmentNote:
          return (actionEffect as AddAppointmentNoteActionEffect)?.appointmentNote;
        case ActionEffectType.AddTagsToChannel:
          return (actionEffect as TagConversationActionEffect)?.tags;
        case ActionEffectType.ConfirmAppointment:
          return actionEffect ? ActionEffectType.ConfirmAppointment : ActionEffectType.None;
        case ActionEffectType.ScheduleWorkflow:
          return actionEffect as ExtendedScheduleWorkflowActionEffect;
        case ActionEffectType.CreateTask:
          return actionEffect as CreateTaskActionEffect;
        default:
          return undefined;
      }
    },
    [buttonDraft],
  );

  const buttonActionMenuItems: MenuItemProps[] = useMemo(() => {
    const items: MenuItemProps[] = [
      {
        label: automationActionLabels.setConversationStatus,
        value: AutomationButtonAction.SetConversationStatus,
        isSelected: selectedButtonActions.includes(AutomationButtonAction.SetConversationStatus),
      },
      {
        label: automationActionLabels.setAppointmentStatus,
        value: AutomationButtonAction.SetAppointmentStatus,
        isSelected: selectedButtonActions.includes(AutomationButtonAction.SetAppointmentStatus),
      },
      {
        label: automationActionLabels.addConversationTags,
        value: AutomationButtonAction.AddConversationTags,
        isSelected: selectedButtonActions.includes(AutomationButtonAction.AddConversationTags),
      },
      {
        label: automationActionLabels.scheduleWorkflow,
        value: AutomationButtonAction.ScheduleWorkflow,
        isSelected: selectedButtonActions.includes(AutomationButtonAction.ScheduleWorkflow),
      },
    ];

    if (isFeatureEnabled(FeatureFlagName.Tasks)) {
      items.push({
        label: automationActionLabels.createTask,
        value: AutomationButtonAction.CreateTask,
        isSelected: selectedButtonActions.includes(AutomationButtonAction.CreateTask),
      });
    }

    return items;
  }, [isFeatureEnabled, selectedButtonActions]);

  const availableButtonActionMenuItems = useMemo(
    () => buttonActionMenuItems.filter((item) => !item.isSelected),
    [buttonActionMenuItems],
  );

  const onCreateTaskEffectChange = useCallback(
    (actionEffect: CreateTaskActionEffect) => {
      const updatedEffects: ActionEffect[] = [
        ...(buttonDraft.actionEffects?.filter((effect) => effect.type !== ActionEffectType.CreateTask) || []),
        actionEffect,
      ];

      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: { actionEffects: updatedEffects },
        }),
      );
    },
    [buttonDraft.actionEffects, dispatch, promptIndex, buttonIndex],
  );

  const onSelectedTagsChange = useCallback(
    (actionEffects: ActionEffect[]) => {
      const updatedEffects: ActionEffect[] = [
        ...(buttonDraft.actionEffects?.filter((effect) => effect.type !== ActionEffectType.AddTagsToChannel) || []),
        ...actionEffects,
      ];
      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: { actionEffects: updatedEffects },
        }),
      );
    },
    [buttonDraft.actionEffects, dispatch, promptIndex, buttonIndex],
  );

  const selectedTags = useMemo(() => {
    return (
      buttonDraft.actionEffects
        ?.filter((effect) => effect.type === ActionEffectType.AddTagsToChannel)
        .flatMap((effect) => (effect as TagConversationActionEffect).tags) || []
    );
  }, [buttonDraft.actionEffects]);

  const onApptNotesChange = useCallback(
    (notes: string) => {
      const actionEffects: ActionEffect[] = [
        ...(buttonDraft.actionEffects?.filter((effect) => effect.type !== ActionEffectType.AddAppointmentNote) || []),
        {
          type: ActionEffectType.AddAppointmentNote,
          appointmentNote: notes,
        },
      ];

      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: { actionEffects },
        }),
      );
    },
    [buttonDraft.actionEffects, dispatch, promptIndex, buttonIndex],
  );

  const removeAppointmentNotes = useCallback(() => {
    const nonApptNoteActionEffect = buttonDraft.actionEffects?.filter(
      (effect) => effect.type !== ActionEffectType.AddAppointmentNote,
    );
    const buttonConfig = { actionEffects: nonApptNoteActionEffect };
    dispatch(
      updateAutomationPromptButton({
        promptIndex,
        buttonIndex,
        button: {
          ...buttonConfig,
        },
      }),
    );
  }, [buttonDraft.actionEffects, buttonIndex, dispatch, promptIndex]);

  const handleToggleAppointmentNotes = useCallback(() => {
    if (showAppointmentNotes) {
      removeAppointmentNotes();
    }
    setShowAppointmentNotes((current) => !current);
  }, [removeAppointmentNotes, showAppointmentNotes]);

  const isConfirmingAppointment = useMemo(
    () => getDraftActionEffectValue(ActionEffectType.ConfirmAppointment) === ActionEffectType.ConfirmAppointment,
    [getDraftActionEffectValue],
  );

  const apptNotesText = useMemo(
    () => getDraftActionEffectValue(ActionEffectType.AddAppointmentNote) as string,
    [getDraftActionEffectValue],
  );

  const taskCreationEffectData = useMemo(
    () => getDraftActionEffectValue(ActionEffectType.CreateTask) as CreateTaskActionEffect,
    [getDraftActionEffectValue],
  );

  const onScheduledWorkflowIdChange = useCallback(
    (workflowId: string | undefined, workflowType?: WorkflowEvent | null) => {
      if (!workflowId) return;

      const existingActionDelay = convertToSeconds({
        amount: scheduledWorkflow?.delayAmount ?? 0,
        increment: scheduledWorkflow?.delayIncrement ?? TimeIncrement.Hours,
      });

      const actionEffects: ActionEffect[] = [
        ...(buttonDraft.actionEffects?.filter((effect) => effect.type !== ActionEffectType.ScheduleWorkflow) || []),
        {
          type: ActionEffectType.ScheduleWorkflow,
          workflowId,
          workflowType,
          delayInSeconds: existingActionDelay,
        } as ExtendedScheduleWorkflowActionEffect,
      ];

      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: {
            actionEffects,
          },
        }),
      );
    },
    [buttonDraft.actionEffects, buttonIndex, dispatch, promptIndex, scheduledWorkflow],
  );

  const onScheduledWorkflowDelayChange = useCallback(
    (delay: number | undefined, increment: TimeIncrement | undefined) => {
      if (delay === undefined || increment === undefined) return;

      const delayInSeconds = convertToSeconds({ amount: delay, increment });
      const actionEffects: ActionEffect[] = [
        ...(buttonDraft.actionEffects?.filter((effect) => effect.type !== ActionEffectType.ScheduleWorkflow) || []),
        {
          type: ActionEffectType.ScheduleWorkflow,
          delayInSeconds: delayInSeconds,
          workflowId: scheduledWorkflow?.id || '',
          workflowType: scheduledWorkflow?.workflowType,
        } as ExtendedScheduleWorkflowActionEffect,
      ];

      dispatch(
        updateAutomationPromptButton({
          promptIndex,
          buttonIndex,
          button: {
            actionEffects,
          },
        }),
      );
    },
    [buttonDraft.actionEffects, buttonIndex, dispatch, promptIndex, scheduledWorkflow],
  );

  const handleModalClose = useCallback(
    (revertChanges?: boolean) => {
      if (revertChanges) {
        onClose(true);
        return;
      }

      // Validations

      const actionErrorChecks: {
        action: AutomationButtonAction;
        condition: boolean;
        errors: Record<string, boolean>;
      }[] = [
        {
          action: AutomationButtonAction.CreateTask,
          condition: !taskCreationEffectData?.taskName,
          errors: { taskName: true },
        },
        {
          action: AutomationButtonAction.ScheduleWorkflow,
          condition: !scheduledWorkflow?.id || !scheduledWorkflow?.delayAmount,
          errors: {
            workflowType: !scheduledWorkflow?.id,
            delayAmount: !scheduledWorkflow?.delayAmount,
          },
        },
        {
          action: AutomationButtonAction.SetAppointmentStatus,
          condition: getDraftActionEffectValue(ActionEffectType.ConfirmAppointment) === ActionEffectType.None,
          errors: { noStatus: true },
        },
        {
          action: AutomationButtonAction.AddConversationTags,
          condition: !selectedTags.length,
          errors: { noTags: true },
        },
      ];

      let errorCount = 0;
      for (const { action, condition, errors } of actionErrorChecks) {
        const errorFound = selectedButtonActions.includes(action) && condition;
        setButtonActionErrors(action, errorFound ? errors : {});
        if (errorFound) {
          errorCount++;
        }
      }

      if (errorCount) {
        return;
      }

      // dispatch 'schedule automation' updates on close instead of immediately
      // since we don't store the increment separately and we update it to smallest number on open
      const hasScheduleWorkflowAction = selectedButtonActions?.some(
        (action) => action === AutomationButtonAction.ScheduleWorkflow,
      );

      if (hasScheduleWorkflowAction) {
        onScheduledWorkflowIdChange(scheduledWorkflow?.id, scheduledWorkflow?.workflowType);
        onScheduledWorkflowDelayChange(scheduledWorkflow?.delayAmount, scheduledWorkflow?.delayIncrement);
      }

      onClose(false);
    },
    [
      selectedButtonActions,
      getDraftActionEffectValue,
      taskCreationEffectData?.taskName,
      scheduledWorkflow?.id,
      scheduledWorkflow?.delayAmount,
      scheduledWorkflow?.workflowType,
      scheduledWorkflow?.delayIncrement,
      selectedTags.length,
      onClose,
      setButtonActionErrors,
      onScheduledWorkflowIdChange,
      onScheduledWorkflowDelayChange,
    ],
  );

  const getButtonActionChildComponent = useCallback(
    (buttonAction: AutomationButtonAction) => {
      switch (buttonAction) {
        case AutomationButtonAction.SetAppointmentStatus:
          return (
            <VStack gap={1}>
              <Select
                size="md"
                w="231px"
                value={getDraftActionEffectValue(ActionEffectType.ConfirmAppointment) as string | undefined}
                options={buttonActionOptions}
                listProps={{ onSelect: handleSetAppointmentStatusChange }}
                isInvalid={buttonActionErrors[AutomationButtonAction.SetAppointmentStatus]?.noStatus}
                errorText="Please select a status"
              />
              {isConfirmingAppointment && isWritebackApptNotesSupported && (
                <VStack w="100%" alignItems="flex-start">
                  {showAppointmentNotes && (
                    <Textarea
                      size="md"
                      minH={20}
                      placeholder="Add appointment notes..."
                      value={apptNotesText as string | undefined}
                      onChange={(e): void => onApptNotesChange(e.currentTarget.value)}
                    />
                  )}
                  <Button
                    size="sm"
                    iconName={showAppointmentNotes ? 'trashcan' : 'plus'}
                    variant={showAppointmentNotes ? 'ghostDestructive' : 'ghost'}
                    onClick={handleToggleAppointmentNotes}
                  >
                    {showAppointmentNotes ? 'Remove Appointment Notes' : 'Add Appointment Notes'}
                  </Button>
                </VStack>
              )}
            </VStack>
          );
        case AutomationButtonAction.SetConversationStatus:
          return (
            <StatusChangeSelect
              size="md"
              buttonWidth="231px"
              showLabel={false}
              promptType={promptType}
              promptIndex={promptIndex}
              buttonIndex={buttonIndex}
              channelStatusChangeId={button.channelStatusChangeId}
              errors={buttonActionErrors[AutomationButtonAction.SetConversationStatus]}
            />
          );
        case AutomationButtonAction.AddConversationTags:
          return (
            <ConversationTagMenu
              selectedTags={selectedTags}
              onSelectedTagsChange={onSelectedTagsChange}
              errors={buttonActionErrors[AutomationButtonAction.AddConversationTags]}
            />
          );
        case AutomationButtonAction.ScheduleWorkflow:
          return (
            <ScheduledWorkflowSelector
              scheduledWorkflow={scheduledWorkflow}
              setScheduledWorkflow={setScheduledWorkflow}
              errors={buttonActionErrors[AutomationButtonAction.ScheduleWorkflow]}
            />
          );
        case AutomationButtonAction.CreateTask:
          return (
            <CreateTaskForm
              taskCreationEffectData={taskCreationEffectData}
              onCreateTaskEffectChange={onCreateTaskEffectChange}
              errors={buttonActionErrors[AutomationButtonAction.CreateTask]}
            />
          );
        default:
          return null;
      }
    },
    [
      getDraftActionEffectValue,
      handleSetAppointmentStatusChange,
      isConfirmingAppointment,
      isWritebackApptNotesSupported,
      showAppointmentNotes,
      apptNotesText,
      handleToggleAppointmentNotes,
      promptType,
      promptIndex,
      buttonIndex,
      button.channelStatusChangeId,
      selectedTags,
      onSelectedTagsChange,
      scheduledWorkflow,
      taskCreationEffectData,
      onCreateTaskEffectChange,
      buttonActionErrors,
      onApptNotesChange,
    ],
  );

  const buttonTextInputRef = useCallback((el: HTMLInputElement | null): void => {
    if (el) {
      el.focus();
    }
  }, []);

  if (!buttonDraft) {
    return null;
  }

  return (
    <Modal size="xl" isOpen={isOpen} onClose={partial(onClose, false)} scrollBehavior="outside" isCentered={false}>
      <ModalHeader size="lg">Edit Button</ModalHeader>
      <ModalCloseButton size="sm" />
      <ModalBody pb={2}>
        <VStack spacing={4} align="stretch">
          <EditButtonModalSectionHeader
            title="Button Style"
            description="Change button text and style. Tip: Keep it short, sweet, and don't go overboard with styles."
          />
          <HStack alignItems="flex-start">
            <TextInput
              ref={buttonTextInputRef}
              label="Button Text"
              labelStyle={{
                fontWeight: 'semibold',
              }}
              size="md"
              isInvalid={Boolean(buttonTextErrors?.length)}
              errorText={buttonTextErrors?.map((errorName) => errorDetails[errorName].errorMessage).join(' ')}
              value={buttonDraft.body}
              onChange={handleButtonTextChange}
              placeholder="Button text"
            />
            <Select
              label="Button Style"
              labelStyle={{
                fontWeight: 'semibold',
              }}
              size="md"
              value={buttonDraft.style || AutomationButtonVariant.Secondary}
              options={buttonStyleOptions}
              listProps={{ onSelect: handleButtonStyleChange }}
            />
          </HStack>
          <VStack bg="background.subtle" borderRadius={12} pt={2} pb={3} px={3}>
            <Text variant="subtle">Preview</Text>
            <Button
              variant={buttonDraft.style || AutomationButtonVariant.Secondary}
              h="max-content"
              whiteSpace="break-spaces"
              iconName={buttonDraft.externalUrl !== undefined ? 'externalLink' : undefined}
            >
              {buttonDraft.body}
            </Button>
          </VStack>
          <EditButtonModalSectionHeader
            title="Button Actions"
            description="Make changes to conversations, appointments, and schedule automations to send later. Button actions are optional."
            pt={4}
          />
          <VStack gap={1}>
            {selectedButtonActions.map((buttonAction, index) => {
              return (
                <ButtonActionContainer
                  key={`buttonAction-${index}`}
                  label={automationActionLabels[buttonAction]}
                  description={automationActionDescriptions[buttonAction]}
                  remove={(): void => removeButtonAction(buttonAction)}
                >
                  {getButtonActionChildComponent(buttonAction)}
                </ButtonActionContainer>
              );
            })}
          </VStack>
          {availableButtonActionMenuItems.length && (
            <Menu
              buttonProps={{
                text: 'Add Button Action',
                variant: 'ghost',
                size: 'md',
                leftIconName: 'plus',
                showRightIcon: false,
                w: '194px',
                alignSelf: 'flex-start',
              }}
              listProps={{
                menuItems: availableButtonActionMenuItems,
                onSelect: (selectedItem): void => handleButtonActionSelected(selectedItem),
              }}
              closeOnSelect={true}
            />
          )}
          <EditButtonModalSectionHeader
            title="Next Step"
            description="Define what happens next after the button is tapped."
            pt={4}
          />
          <NextStepSelect
            promptIndex={promptIndex}
            buttonIndex={buttonIndex}
            showLabel={false}
            buttonUrlErrors={buttonUrlErrors}
          />
        </VStack>
      </ModalBody>
      <ModalFooterButtons
        onSubmit={partial(handleModalClose, false)}
        onCancel={partial(handleModalClose, true)}
        submitText="Done"
      />
    </Modal>
  );
};

export default withLazyModal(EditButtonModal);
